/**
 * Internal barrel for the portal subtree of
 * `@akademi/site-builder-runtime`. Holds renderer, types, contexts,
 * theme tokens, i18n, menu helpers, and the host-adapter contracts —
 * the platform-layer primitives the shells (in `../shells/`) and the
 * base theme (in `../theme/`) build on, and that downstream theme
 * packages depend on via this package's public {@link ../index}.
 */

export type {
  LocaleTag,
  LocalizedString,
  BlockInstance,
  SlotValue,
  BlockVisibilityProps,
  PageConfig,
  SlotSpec,
  SystemKind,
  BlockComponent,
  SystemComponent,
  PrimitiveKind,
  PrimitiveComponent,
  ThemeBundle,
  ResolverCatalog,
  BlockResolver,
  ArticleBlock,
  ArticleBlockPlacement,
} from './types';

export { sbText, resolveClientI18n, type I18nReader } from './i18nCatalogue';
export { localizeTree } from './localize';
export { tx, asList, asNumber, asBool, asObject, pickEnum } from './coerce';
export {
  encodeHtmlPayload,
  decodeHtmlPayload,
  isHtmlPayloadEncoded,
  HTML_PAYLOAD_PREFIX,
} from './htmlPayload';
export {
  coverBackground,
  coverGradientBackground,
  coverWithThemedOverlay,
  coverHexSeed,
} from './coverStyle';
export { parseMarkdown, countParagraphs, paragraphSlots, type MdBlock } from './articleBody';
export { pageBleedsUnderHeader, type BleedAwareComponent } from './pageBleed';
export {
  BLOG_DEFAULTS,
  WIKI_DEFAULTS,
  MICRO_DEFAULTS,
  VIDEO_DEFAULTS,
  mergeBlogConfig,
  mergeWikiConfig,
  mergeMicroConfig,
  mergeVideoConfig,
  resolveBlogSearchPlacement,
  resolveWikiSearchPlacement,
  resolveVideoSearchPlacement,
  resolveMicroSearchPlacement,
  resolveMicroListingChrome,
  readContentModuleConfig,
  writeContentModuleConfig,
  type ContentModuleKey,
  type MicroListingChrome,
  type BlogModuleConfig,
  type NewsModuleConfig,
  type WikiModuleConfig,
  type MicroModuleConfig,
  type VideoModuleConfig,
  type ResolvedBlogConfig,
  type ResolvedWikiConfig,
  type ResolvedMicroConfig,
  type ResolvedVideoConfig,
  type ChromeMode,
  type CategoriesPlacement,
  type ModuleSearchConfig,
  type ArticleTemplate,
} from './contentModuleConfig';
export { MicroModuleConfigProvider, useMicroModuleConfig } from './microModuleConfig';
export {
  unwrapStrapiSingle,
  unwrapStrapiCollection,
  unwrapStrapiRaw,
  buildStrapiQuery,
  type StrapiPagination,
  type StrapiCollectionEnvelope,
  type StrapiSingleEnvelope,
  type StrapiRawEnvelope,
  type UnwrappedCollection,
  type StrapiQuerySpec,
} from './strapi';

export {
  AuthContext,
  LocaleContext,
  ThemeContext,
  BlockResolverContext,
  BlockResolverProvider,
  PluginGateContext,
  PluginGateProvider,
  MessagesContext,
  MessagesProvider,
  BrandContext,
  useCurrentUser,
  useLocale,
  useThemeId,
  useBlockResolver,
  useEnabledPlugins,
  useMessage,
  useMessages,
  useBrand,
} from './contexts';
export type { ShellMessages, BrandInfo } from './contexts';

export {
  DEFAULT_BLOCK_ATTR_NS,
  BLOCK_PARTS,
  BlockAttrConfigProvider,
  BlockIdentityContext,
  BlockRootStamped,
  BlockRootStampedContext,
  useBlockAttrNamespace,
  useBlockIdentity,
  useBlockRootAttrs,
  useBlockPart,
  useBlockControlStamp,
  assignControlHandles,
  ownedPanelIds,
} from './blockIdentity';
export type {
  BlockAttrConfig,
  BlockIdentity,
  BlockDataAttrs,
  BlockPartName,
} from './blockIdentity';
export { assignBlockIds, blockBaseId, slugify, KEYISH_PROPS } from './blockId';
export { BlockRoot, type BlockRootProps } from './BlockRoot';

export { BlockErrorBoundary } from './BlockErrorBoundary';
export { createBlockResolver } from './createBlockResolver';
export { useChildrenRenderer } from './renderChildren';
export { BlockRenderer, RenderOneBlock, type BlockRendererProps } from './BlockRenderer';
export { BlockList } from './BlockList';
export { Island, invalidateIsland } from './Island';
export { mergeArticleBlocks, groupArticleBlocks, type GroupedBlocks } from './articleBlocks';
export {
  useArticleBlockGroups,
  ArticleTopBlocks,
  ArticleBottomBlocks,
  ArticleSidebarBlocks,
  IndexAboveBlocks,
  IndexBelowBlocks,
} from './ArticleBlockZones';

export {
  tokensToCss,
  readFontImports,
  type TokenNode,
  type TokenLeaf,
  type TokenGroup,
  type FontImport,
} from './themeToCss';
export { ThemeTokens } from './ThemeTokens';
export { PortalLink, type PortalLinkProps } from './PortalLink';
export {
  StoreBadge,
  AppStoreBadgeArt,
  GooglePlayBadgeArt,
  type StoreBadgeProps,
  type StoreKind,
} from './StoreBadge';

export type {
  ShellConfig,
  BrandConfig,
  HeaderConfig,
  HeaderStyle,
  HeaderAction,
  ProfileMenuEntry,
  ProfileMenuLink,
  ProfileMenuGroup,
  FooterConfig,
  FooterStyle,
  FooterGroup,
  FooterLink,
  FooterSocial,
  FooterAppBadge,
  FooterUtilityLink,
  FooterResourceListConfig,
  TopBarConfig,
  TopBarTone,
  ShellFrame,
  ThemeDefaultsConfig,
  ScrollToTopConfig,
  ScrollToTopPosition,
  AnalyticsIntegrationsConfig,
  HelpDeskWidgetConfig,
  HelpDeskCategory,
  SearchSectionConfig,
  SearchSectionLabels,
  SearchCategoryOption,
} from './shellTypes';
export { SEARCH_WIDTH_PRESETS_PX } from './shellTypes';

export type { CurrentUser } from './auth-types';
export {
  t,
  isVisible,
  filterVisible,
  DEFAULT_I18N,
  type I18nConfig,
  type LanguageSwitcherStyle,
  type AuthRequirement,
  type Gated,
} from './i18n';
export type {
  MenuEntry,
  MenuLink,
  MenuGroup,
  MenuComponentEntry,
  MegaMenuConfig,
} from './menuTypes';
export { flattenMenu, flattenMenuWithComponents, mergeMenuComponentProps } from './menu';
export { blockBleedsUnderHeader } from './blockMetadata';
export {
  LANGUAGES,
  NATIVE_NAMES_FROM_REGISTRY,
  ENGLISH_NAMES_FROM_REGISTRY,
  findLanguage,
  type LanguageEntry,
} from './languages';

export {
  NotificationsSourceProvider,
  useNotificationsSource,
  NOTIFICATIONS_REFRESH_EVENT,
  type NotificationsSource,
  type NotificationsPage,
  type NotificationItem,
} from './notificationsSource';
export {
  GamificationSourceProvider,
  useGamificationSource,
  GAMIFICATION_REFRESH_EVENT,
  type GamificationSource,
  type GamificationSummary,
} from './gamificationSource';
export {
  SearchSourceProvider,
  useSearchSource,
  type SearchSource,
  type SearchSuggestion,
} from './searchSource';
export {
  BlogSourceProvider,
  useBlogSource,
  type BlogSource,
  type Article,
  type BlogCategory,
  type BlogQuerySpec,
  type BlogPagination,
} from './blogSource';
export {
  CommerceSourceProvider,
  useCommerceSource,
  type CommerceSource,
  type MoneyValue,
  type CommerceImage,
  type CommerceAvailability,
  type ProductCard,
  type ProductDetail,
  type ProductAttribute,
  type ProductDocument,
  type CategoryNode,
  type BrandCard,
  type Facet,
  type FacetValue,
  type ProductListingQuery,
  type ProductListing,
  type DecimalString,
  type Cart,
  type CartLine,
  type CartAddition,
  type CartFulfilment,
  type PaymentOptions,
  type PaymentRequest,
  type TestInstrument,
  type PaymentResult,
  type ShippingOption,
  type CheckoutAddress,
  type CheckoutRequest,
  type Order,
  type OrderLine,
  type OrderLookup,
  AccountError,
  type AccountErrorKind,
  type AccountProfile,
  type AccountOrderSummary,
  type AccountAddress,
  type AccountAddressInput,
  type CollectionPoint,
  type OrderReturn,
  type OrderReturnLine,
  type ReturnReason,
  type ReturnRequestInput,
  type ProductReview,
  type ProductReviewSummary,
  type ProductReviewPage,
  type ReviewSubmission,
  type ReviewReceipt,
  type RegisterRequest,
  type AccountActivationRequest,
  type SignInRequest,
  type EmailChangeRequest,
  type PasswordResetRequest,
} from './commerceSource';
export { CartProvider, useCart, type CartState } from './cartState';
export {
  MarketplaceSourceProvider,
  useMarketplaceSource,
  type MarketplaceSource,
  type MarketplaceCatalogCard,
  type MarketplaceCatalogListing,
  type MarketplaceCatalogListingQuery,
  type MarketplaceCourseCard,
  type MarketplaceCourseListingQuery,
  type MarketplaceCourseListing,
  type MarketplaceCartItem,
  type MarketplaceAppliedCoupon,
  type MarketplaceCart,
  type MarketplaceOrderStatus,
  type MarketplaceOrder,
  type MarketplaceOrderListingQuery,
  type MarketplaceOrderListing,
  type MarketplaceBankTransferDetails,
  type MarketplaceBookmarkKind,
} from './marketplaceSource';
export {
  fetchFaqPage,
  fetchFaqAnswer,
  fetchFaqCategories,
  fetchCourseFaqs as fetchRichCourseFaqs,
  type FaqEntry,
  type FaqPage,
  type FaqPageQuery,
  type FaqCategory,
} from './faqSource';
export {
  LearningSourceProvider,
  useLearningSource,
  detectLearningItemKind,
  type LearningSource,
  type EnrollmentSource,
  type LearningItem,
  type LearningItemKind,
  type LearningCourseDetail,
  type LearningChapterDetail,
  type LearningCourseFaq,
  type LearningCourseDocument,
  type LearningCourseComment,
  type LearningInstructorRef,
  type LearningRow,
  type LearningJourney,
  type LearningJourneyCourse,
  type LearningArticle,
  type LearningEvent,
  type LearningCategory,
  type Enrollment,
  type CurrentCourse,
  type CourseSearchFilter,
  type CourseSearchOpts,
  type CourseSearchResult,
} from './learningSource';
export {
  WikiSourceProvider,
  useWikiSource,
  findWikiPath,
  type WikiSource,
  type WikiPage,
  type WikiTreeNode,
  type WikiQuerySpec,
  type WikiPagination,
} from './wikiSource';
export {
  CategoryTreeSourceProvider,
  useCategoryTreeSource,
  findCategoryPath,
  type CategoryTreeSource,
  type CategoryTreeNode,
  type CategoryTreeType,
} from './categoryTreeSource';
export {
  MediaSourceProvider,
  useMediaSource,
  type MediaSource,
  type MediaItem,
  type MediaVideoFull,
  type MediaVideoDetail,
  type MediaSeriesFull,
  type MediaSeriesDetail,
  type MediaEpisode,
  type ChannelCard,
  type ChannelDetail,
  type ChannelSort,
  type MediaCategory,
  type MediaComment,
  type MediaSourceSlug,
  type MediaVideosNamespace,
  type MediaSeriesNamespace,
  type MediaChannelsNamespace,
  type MediaCategoriesNamespace,
  type MediaCommentsNamespace,
  type MediaLibraryNamespace,
  type MediaSubscriptionsNamespace,
} from './mediaSource';
export {
  UserResourceSourceProvider,
  useUserResourceSource,
  UserResourceAnonymousError,
  type UserResourceSource,
  type VideoProgress,
  type ToggleResult,
  type WishlistNamespace,
  type WatchLaterNamespace,
  type LikesNamespace,
  type CourseWatchLaterNamespace,
  type SubscriptionsNamespace,
  type WatchProgressNamespace,
  type HistoryNamespace,
  type CourseProgressNamespace,
  type RecommendationsNamespace,
  type ReviewsNamespace,
  type MyCoursesNamespace,
  type MyCourseItem,
  type ApprovalDetail,
  type ApprovalApprover,
  type PendingFormsNamespace,
  type PendingFormItem,
  type DemandFormNamespace,
  type DemandFormItem,
  type DemandFormCategory,
  type DemandFormCurrency,
  type DemandFormNewRequest,
  type DemandFormEmployee,
  type DemandFormApprovalDetail,
  type AddressesNamespace,
  type UserAddress,
} from './userResourceSource';
export {
  MenuDataSourceProvider,
  useMenuDataSource,
  type MenuDataSource,
  type MenuCategoryDomain,
} from './menuDataSource';
export {
  MicroSourceProvider,
  useMicroSource,
  MICRO_KIND_BY_SLUG,
  isKnownMicroKindSlug,
  type MicroSource,
  type MicroContentItem,
  type MicroContentKind,
  type MicroCategorySummary,
  type MicroContentTypeSummary,
  type MicroProgress,
  type MicroProgressSummary,
  type MicroContentNamespace,
  type MicroCategoriesNamespace,
  type MicroContentTypesNamespace,
  type MicroProgressNamespace,
  type MicroRecommendationsNamespace,
  type MicroFavoritesNamespace,
  type MicroPollResults,
  type MicroPollsNamespace,
} from './microSource';

export type {
  MicroScenarioBody,
  MicroScenarioNode,
  MicroScenarioChoice,
  MicroScenarioOutcome,
  MicroInfoCardsBody,
  MicroInfoCardStep,
  MicroWeeklyReadBody,
  MicroWeeklyReadStructure,
  MicroDownloadBody,
  MicroCardFlipFaces,
  MicroInteractiveCardFlipBody,
  MicroMatchingExercise,
  MicroInteractiveMatchingBody,
  MicroDragDropTarget,
  MicroDragDropItem,
  MicroDragDropExercise,
  MicroInteractiveDragDropBody,
} from './microBodies';

// — System primitives (theme-overridable card / chrome contracts) ————————
export {
  PageContainer,
  cardStyle,
  muted,
  CourseCard as DefaultCourseCard,
  ArticleCard as DefaultArticleCard,
  EventCard as DefaultEventCard,
  CategoryCard as DefaultCategoryCard,
  PageHeader as DefaultPageHeader,
  Pill as DefaultPill,
  EmptyState as DefaultEmptyState,
} from './SystemUI';
export type {
  SystemPrimitives,
  SystemPrimitiveKind,
  CourseCardProps,
  ArticleCardProps,
  EventCardProps,
  CategoryCardProps,
  PageHeaderProps,
  PillProps,
  EmptyStateProps,
  BlogHeroProps,
  WikiPageHeaderProps,
} from './SystemPrimitives.types';

// — Theme registry (host-driven; resolver mechanism, no concrete themes) —
export {
  registerTheme,
  unregisterTheme,
  getTheme,
  listThemes,
  resetThemes,
  resolveBlock as resolveBlockFromRegistry,
  resolveSystem as resolveSystemFromRegistry,
  resolveSystemPrimitive as resolveSystemPrimitiveFromRegistry,
  resolveThemedHeader,
  resolveThemedFooter,
  getResolverThemes,
  type ThemeBundle as RegistryThemeBundle,
} from './themeRegistry';

// Server-safe resolver aliases — same registry, kept under their legacy
// names so portal call sites that imported from
// `@/lib/platform/resolveSystem` / `resolveSystemPrimitive` keep working
// after the move into the runtime.
export { resolveSystem, type SystemKind as ResolverSystemKind } from './resolveSystem';
export { resolveSystemPrimitive, tryResolveSystemPrimitive } from './resolveSystemPrimitive';

// — SEO canonical URL helpers ——————————————————————————————————————————
export {
  canonicalOrigin,
  canonicalTenantRoot,
  canonicalTenantPage,
  canonicalCatalog,
  canonicalCourseDetail,
  canonicalVideoDetail,
  canonicalBlogPost,
  canonicalWikiPage,
  canonicalBlogIndex,
  canonicalWikiIndex,
  canonicalVideoIndex,
  tenantSitemapUrl,
  tenantVideoSitemapUrl,
  absoluteUrl,
} from './canonical';

// — Course URL scheme (legacy-matched: detail singular, hub plural) ——————
export {
  toFriendlyCourseSlug,
  courseDetailHref,
  catalogSearchHref,
  COURSE_DETAIL_BASE,
  MY_COURSES_BASE,
  CATALOG_BASE,
} from './courseUrl';
